// 3rd-party libraries
import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { withMsal } from '@azure/msal-react';
import { push } from 'connected-react-router';
import { Formik } from 'formik';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as Yup from 'yup';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import FormHelperText from '@material-ui/core/FormHelperText';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import { updateSession } from 'global/actions/global-actions';
import axios from 'global/axios-instance';

// constants
import UiConfig from 'constants/ui-config';

// static assets
import googleLogo from 'assets/google_logo.png';
import laerdalLogo from 'assets/laerdal_logo.png';
import jausLogo from 'assets/jaus_new_200.png';

const schema = Yup.object().shape({
    email: Yup.string()
        .required('Please enter your email')
        .email('Invalid email address'),
    password: Yup.string()
        .required('Please enter your password')
});

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        updateSession,
        push
    }, dispatch);
}

const styles = theme => ({
    container: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: '350px'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    heading: {
        textAlign: 'center'
    },
    textField: {
        margin: theme.spacing(1),
        width: 200
    },
    dense: {
        marginTop: 19
    },
    menu: {
        width: 200
    },
    button: {
        margin: theme.spacing(1)
    },
    sectionHeader: {
        textAlign: 'center',
        margin: '1rem 0 1rem 0',
        fontSize: 14
    },
    divider: {
        width: '100%',
        marginTop: '1rem'
    },
    logo: {
        width: '150px',
        height: '150px'
    },
    googleButton: {
        textTransform: 'none',
        color: '#FFFFFF',
        backgroundColor: '#4c8bf5',
        paddingLeft: '0.1rem',
        paddingTop: '0.12rem',
        paddingBottom: '0.12rem',
        marginBottom: '0.5rem'
    },
    laerdalButton: {
        textTransform: 'none',
        color: '#FFFFFF',
        backgroundColor: '#FF0000',
        paddingLeft: '0.12rem',
        paddingTop: '0.12rem',
        paddingBottom: '0.12rem'
    },
    image: {
        width: '35px',
        height: '33px',
        marginRight: '0.5rem'
    }
});

@withStyles(styles)
@connect(null, mapDispatchToProps)
@withMsal
class Login extends React.PureComponent {
    state = {
        credentialsError: null,
        ssoError: null
    };

    static propTypes = {
        updateSession: PropTypes.func.isRequired,
        msalContext: PropTypes.object.isRequired,
        push: PropTypes.func.isRequired,
        classes: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);
        this.doSubmit = this.doSubmit.bind(this);
        this.doGoogleLogin = this.doGoogleLogin.bind(this);
        this.doMicrosoftLogin = this.doMicrosoftLogin.bind(this);
    }

    doSubmit(values, { setSubmitting }) {
        if (!_.isEmpty(this.state.credentialsError)) {
            this.setState({
                credentialsError: null
            });
        }
        axios.post('/auth/challenge', values)
            .then(response => {
                this.props.updateSession({
                    token: response.data.token,
                    user: response.data.user,
                    refreshToken: response.data.refreshToken
                });
                setSubmitting(false);
                const user = response.data.user;
                if ( !user.accessRequestSubmitted || user.awsRegions.length === 0) {
                    return this.props.push('/questionnaire-landing');
                }

                this.props.push('/');
            })
            .catch(err => {
                let credentialsError;
                if (_.isNil(err.response) || _.isNil(err.response.status)) {
                    credentialsError = 'A network error occurred.';
                } else if (err.response.data?.message === 'INVALID_CREDENTIALS'
                    || err.response.data?.message === 'ACCOUNT_DEACTIVATED') {
                    credentialsError = err.response.data.meta.detail;
                } else {
                    credentialsError = 'An unknown error occurred.';
                }
                this.setState({ credentialsError });
                setSubmitting(false);
            });
    }

    async doGoogleLogin() {
        // There's a miniscule chance this isn't loaded by the time the button's clicked. Not
        // if it happens we can move to injecting the gapi code and watching the onLoad function
        window.gapi.auth2.authorize({
            client_id: UiConfig.GAPI_CLIENT_ID,
            scope: 'profile email',
            response_type: 'code',
            hosted_domain: 'blinemedical.com'
        }, async (authorizationResponse) => {
            if (!authorizationResponse.code) {
                // eslint-disable-next-line no-console
                console.error('Code is missing from google auth request. Seems to happen in some very weird situations '
                    + 'due to caching: https://stackoverflow.com/a/45761277');
            }
            const responseData = await axios.post('/auth/google', {
                code: authorizationResponse.code
            });

            const { token, user, refreshToken } = responseData.data;

            this.props.updateSession({
                token,
                user,
                refreshToken
            });

            if (_.isNil(this.props.location.state) || _.isNil(this.props.location.state.from)) {
                // Saw this happen once, not sure why
                // eslint-disable-next-line no-console
                console.warn('state.from is empty');
                return this.props.push('/');
            }

            this.props.push(this.props.location.state.from);
        });
    }

    async doMicrosoftLogin() {
        const { instance, accounts } = this.props.msalContext;

        const request = {
            scopes: ['User.Read'],
            account: accounts[0],
            redirectUri: `${window.location.origin}/blank.html`
        };
        if (!_.isEmpty(this.state.ssoError)) {
            this.setState({
                // Reset the SSO error message on each login attempt.
                // There is no need to worry about the credentialsError message;
                // Users will not have that feature on production.
                ssoError: null
            });
        }

        let response;
        try {
            // acquireTokenSilent() is successful if there is a valid token in
            // the MSAL cache. You can find it in sessionStorage, with the key
            // msal.token.keys.<CLIENT_ID>. See authConfig.js for the client ID.
            response = await instance.acquireTokenSilent(request);
        } catch (acquireTokenSilentError) {
            // acquireTokenSilent() error does not necessarily indicate a
            // problem (e.g. invalid or missing token in MSAL cache), so we
            // should just log the error and proceed to acquireTokenPopup(),
            // where the user will be prompted to enter their credentials.
            if (acquireTokenSilentError instanceof BrowserAuthError) {
                const browserAuthErrorMessage = `acquireTokenSilent() errored with BrowserAuthError `
                    + `(${acquireTokenSilentError.errorCode}), which likely means there is no MSAL account in the `
                    + 'cache. This error is not necessarily a problem. It may just mean the user needs to sign in.';
                window.console.error(browserAuthErrorMessage, acquireTokenSilentError);
            } else if (acquireTokenSilentError instanceof InteractionRequiredAuthError) {
                const interactionRequiredAuthErrorMessage = `acquireTokenSilent() errored with `
                    + `InteractionRequiredAuthError (${acquireTokenSilentError.errorCode}), which means there was an `
                    + `issue acquiring a token silently that requires user interaction.`;
                window.console.error(interactionRequiredAuthErrorMessage, acquireTokenSilentError);
            } else {
                window.console.error(`acquireTokenSilent() errored with an unknown error`, acquireTokenSilentError);
            }
        }

        if (!response) {
            try {
                response = await instance.acquireTokenPopup(request);
            } catch (acquireTokenPopupError) {
                const acquireTokenPopupErrorCode = acquireTokenPopupError.errorCode;
                let customErrorMessage;
                switch (acquireTokenPopupErrorCode) {
                    case 'user_cancelled':
                        customErrorMessage = 'user cancelled Microsoft SSO login';
                        break;
                    case 'interaction_in_progress':
                        customErrorMessage = 'user interaction is already in progress for Microsoft SSO login';
                        break;
                    case 'monitor_window_timeout':
                        customErrorMessage = 'monitorPopupForHash() timed out after waiting for response from '
                            + 'Microsoft SSO login popup';
                        break;
                    case 'popup_window_error':
                    case 'empty_window_error':
                        customErrorMessage = 'browser has blocked the Microsoft SSO login popup';
                        break;
                    default:
                        customErrorMessage = 'unknown error';
                        break;
                }
                window.console.error(
                    `acquireTokenPopup() error during Microsoft SSO login: ${customErrorMessage}`,
                    acquireTokenPopupError
                );
                this.setState({
                    ssoError: `acquireTokenPopup() error: ${customErrorMessage}`
                });
                return;
            }
        }

        // This try/catch is not nested inside an else statement for the
        // if (!response) condition. Either acquireTokenSilent() or
        // acquireTokenPopup() will set the value for the response variable.
        try {
            await this.useTokenResponse(response);
        } catch (useTokenResponseError) {
            const baseErrorMessage = 'useTokenResponse() error using Microsoft SSO token';
            window.console.error(baseErrorMessage, useTokenResponseError);
            this.setState({
                ssoError: `${baseErrorMessage}. Please try again.`
            });
        }
    }

    useTokenResponse = async (response) => {
        let responseData;
        responseData = await axios.post('/auth/microsoft', {
            accessToken: response.accessToken
        });
        const { token, user, refreshToken } = responseData.data;

        this.props.updateSession({
            token,
            user,
            refreshToken
        });

        if (_.isNil(this.props.location.state) || _.isNil(this.props.location.state.from)) {
            return this.props.push('/');
        }

        this.props.push(this.props.location.state.from);
    };

    render() {
        const { classes } = this.props;
        const {
            credentialsError,
            ssoError
        } = this.state;
        return (
            <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={schema}
                onSubmit={this.doSubmit}>
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting
                    /* and other goodies */
                }) => (
                    <Paper className={classes.container}>
                        <div><img className={classes.logo} src={jausLogo} alt='jaus' /></div>
                        <Typography variant='h6' className={classes.sectionHeader}>
                            Sign in with your Laerdal or B-Line Medical e-mail account
                        </Typography>
                        <Button className={classes.googleButton}
                            variant='contained'
                            onClick={this.doGoogleLogin}>
                            <img src={googleLogo} className={classes.image} alt='Sign in with Google' />
                            Sign in with Google
                        </Button>
                        <Button className={classes.laerdalButton}
                            variant='contained'
                            onClick={this.doMicrosoftLogin}>
                            <img src={laerdalLogo} className={classes.image} alt='Sign in with Laerdal' />
                            Sign in with Laerdal
                        </Button>
                        {!_.isEmpty(ssoError) &&
                            <FormHelperText error={true}>{ssoError}</FormHelperText>
                        }
                        {(UiConfig.ENVIRONMENT === 'local' || UiConfig.ENVIRONMENT === 'development') &&
                            <React.Fragment>
                                <Divider className={classes.divider} />
                                <form onSubmit={handleSubmit} className={classes.form}>
                                    <TextField
                                        error={errors.email && touched.email}
                                        label='Email'
                                        type='email'
                                        name='email'
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        className={classes.textField}
                                        helperText={errors.email && touched.email && errors.email}
                                        autoComplete='email'
                                        inputProps={{
                                            'data-test-id': 'login-email-input'
                                        }} />
                                    <TextField
                                        error={errors.password && touched.password}
                                        label='Password'
                                        type='password'
                                        name='password'
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.password}
                                        className={classes.textField}
                                        helperText={errors.password && touched.password && errors.password}
                                        autoComplete='current-password'
                                        inputProps={{
                                            'data-test-id': 'login-password-input'
                                        }} />
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        className={classes.button}
                                        disabled={isSubmitting}
                                        type='submit'
                                        data-test-id='login-submit-button'>
                                        Submit
                                    </Button>
                                    {!_.isEmpty(credentialsError) &&
                                        <FormHelperText error={true}>{credentialsError}</FormHelperText>
                                    }
                                </form>
                            </React.Fragment>
                        }
                    </Paper>
                )}
            </Formik>
        );
    }
}

export default Login;
